import { onDeleteRecurringDialogCloseThunk } from "@common-reducers/CalendarThunks";
import { clearAllPendingCampaignMessagesThunk } from "@common-reducers/CampaignThunks";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { confirmationDialogOptions } from "@common-views/calendar/calendar-view";
import { deleteUserMessage } from "@common/thunks/UserMessagesThunks";
import { MessageConfirmationDialog } from "@extension/context/content/components/add-scheduled-message-button/message-confirmation-dialog";
import { Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { namespace } from "@common/config/constants";
import {
  decideDelete,
  deleteDialogTitle,
  safeDefaultScopeIndex,
  shouldOfferRecurringScope,
} from "./recurring-delete-scope";

interface DeleteMessageButtonProps {
  message: any;
  style?: React.CSSProperties;
  size?: number;
  onDelete?: (message: any) => void;
  showDeleteString?: boolean;
  className?: string;
}

export const DeleteMessageButton: React.FC<DeleteMessageButtonProps> = ({
  message,
  style = {},
  size = 16,
  onDelete,
  showDeleteString = false,
  className = ''
}) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowConfirmation(true);
  };

  const handleConfirmationClose = async (confirmed?: string) => {
    // undefined => Cancel clicked; any string (incl "") => OK clicked.
    if (confirmed === undefined) {
      setShowConfirmation(false);
      return;
    }

    // #149: the delete scope is decided purely from the message + the user's
    // dialog selection. The surface it was opened from can no longer widen a
    // recurring delete to the whole series — the calendar sidebar used to
    // discard the selection and hard-code "All messages", silently destroying
    // every future occurrence.
    const decision = decideDelete(message, confirmed);
    switch (decision.kind) {
      case 'campaign':
        await dispatch(clearAllPendingCampaignMessagesThunk({ campaignId: message._id })).unwrap();
        break;
      case 'recurring':
        await dispatch(onDeleteRecurringDialogCloseThunk({
          value: decision.scope as any,
          event: {
            _id: message._id,
            dueDate: message.dueDate,
            isCampaign: false,
            isRecurring: true,
          },
        })).unwrap();
        break;
      case 'single':
        await dispatch(deleteUserMessage(message._id)).unwrap();
        break;
    }

    onDelete?.(message);
    setShowConfirmation(false);
  };

  // Base button content
  const buttonContent = (
    <>
      <Trash2 size={size} />
      {showDeleteString && <span>{t('common.delete', 'Delete')}</span>}
    </>
  );

  // #149: offer the scope choice for EVERY recurring message (this used to be
  // suppressed in the calendar sidebar, so the customer got a plain, singular
  // "Delete message?" confirm and lost the whole series).
  const showOptions = shouldOfferRecurringScope(message);

  return (
    <>
      <div
        style={style}
        className={className}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
        /* Not `title`: native tooltips are banned across these surfaces (they
           were removed deliberately in favour of hover cards). The accessible
           name survives as aria-label. */
        aria-label={t('scheduledMessage.deleteMessage', 'Delete message')}
      >
        {buttonContent}
      </div>

      {showConfirmation && (
        <MessageConfirmationDialog
          title={deleteDialogTitle(message)}
          onClose={handleConfirmationClose}
          shouldShowCancelButton={true}
          options={showOptions ? confirmationDialogOptions : undefined}
          // Default to the least-destructive scope ("This message") so an
          // accidental confirm can never nuke the series.
          selected={showOptions ? safeDefaultScopeIndex(confirmationDialogOptions) : undefined}
        />
      )}
    </>
  );
};
